import { TableDataResp } from '../type';
import { getWeeklyAccountTable } from '@op/services';
import { useTablePropsInfo } from '@op/hooks/common';
import { useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ, DBO_U } from '@op/utils/format';
import { useHistory } from 'umi';
import { getAccountValue } from '@op/components/FilterCommon/utils/monitorUtil';
import useStorageOperation from '@op/hooks/useStorageOperation';
import { useUpdateEffect } from 'ahooks';

interface Props {
  filter: any;
  defaultSelectAccount: string[];
}

export function useTableData({ filter, defaultSelectAccount }: Props) {
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    defaultLoading: false,
  });
  const history = useHistory();

  // SessionStorage实例对象
  const { data: storageData, setStore } = useStorageOperation({
    storage: sessionStorage,
    storageKey: 'Opinion-Official-Account-Data-Performance',
    model: 'merge',
    defaultValue: {},
    onlyUseOnce: true,
  });

  const [selectAccounts, setSelectedAccounts] = useState(
    storageData.selectAccounts || defaultSelectAccount,
  );

  // 渠道被选中项
  const [channel, setChannel] = useState(storageData.channel || 'total');

  const [timeObj, setTimeObj] = useState<{
    newTime: string[];
    oldTime: string[];
  }>({ newTime: [], oldTime: [] });

  const {
    total,
    setTotal,
    data,
    setData,
    pageInfo,
    changePageInfo,
    sortBy,
    changeSortBy,
    sortFilter,
    resetPageInfo,
  } = useTablePropsInfo({
    defaultSortBy: { by: 'exposure', order: 'desc' },
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  const params = useMemo(() => {
    const realSelectAccounts =
      selectAccounts.length === defaultSelectAccount.length
        ? []
        : selectAccounts;

    return {
      social_media_by_account: realSelectAccounts,
      channels: channel === 'total' ? [] : [channel],
      ...sortFilter,
      ...pageInfo,
      ...filter,
    };
  }, [pageInfo]);

  useEffect(() => {
    if (defaultSelectAccount.length !== 0) {
      setSelectedAccounts(defaultSelectAccount);
    }
  }, [defaultSelectAccount]);

  // 获取表格全部数据
  function getTableData() {
    setData([]);
    setTotal(0);
    setTimeObj({ newTime: [], oldTime: [] });
    wrapperCancelRequest(getWeeklyAccountTable, params)
      .then((data: TableDataResp) => {
        const { list, total } = data || {};
        let tableRecord = list || [];
        let oldTime: string[] = [];
        if (list && list.length > 0) {
          tableRecord = DBO_REQ.table(list, {
            newListKey: 'new_stats',
            oldListKey: 'old_stats',
            hasQOQ: true,
            hasRATIO: false,
          });
          const item = tableRecord[0];
          oldTime = [item.link_of_start_time, item.link_of_end_time];
        }
        setData(tableRecord);
        setTotal(total || 0);
        setTimeObj({
          newTime: [filter?.start_time, filter?.end_time],
          oldTime,
        });
      })
      .catch(e => console.log(e));
  }

  // 当筛选条件变化时候重新获取表单数据
  useEffect(() => {
    if (filter) {
      getTableData();
    }
  }, [params]);

  useUpdateEffect(() => {
    resetPageInfo();
  }, [filter, channel, selectAccounts]);

  function gotoFeeds(record: any) {
    setStore({
      channel,
      selectAccounts,
      length: history.length,
    });

    const account: any = getAccountValue({
      account: record.account_name,
      channel: record.channel,
    });

    DBO_U.go({
      path: `/opinion/Feeds/Feeds`,
      params: {
        ...filter,
        review_types: ['posts_text', 'posts_video', 'stream_titles'],
        external_data_sources: ['-99999'],
        social_media_by_account: [account],
        social_media_by_keyword: ['-99999'],
      },
    });
  }

  function download() {
    DBO_U.download({
      fileName: 'Official Account Data Performance',
      key: 'OfficialAccountsPerformanceTableDownload',
      filter: params,
    });
  }

  return {
    loading,
    data,
    sortBy,
    changeSortBy,
    changePageInfo,
    pageInfo,
    total,
    setSelectedAccounts,
    selectAccounts,
    setChannel,
    channel,
    timeObj,
    gotoFeeds,
    download,
  };
}
